import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CloseButton, Flex, Paper } from '@mantine/core';
import { modals } from "@mantine/modals";

import PractitionersList from '../PractitionersList';
import MapRouter, { Tab as MapRouterTab } from '../MapRouter';
import OppsAndRisk from '../OppsAndRisks';
import { useLazyLocalStorage } from '../../hooks/useLazyLocalStorage';
import BaseballCard, { Tab as BaseballCardTab } from '../PractitionerBaseballCard';
import { store } from 'stores/Store';

import MapComponent from './MapComponent';
import { FooterMenuViewType } from './components/FooterMenu';
import FunnelMenu from './components/FunnelMenu';
import { DEFAULT_VALUES, FilterControl, withFilterDefaults } from './components/FunnelMenu/FiltersPanel';
import styles from './DesktopLayout.module.scss';
import { ColorType } from './components/FunnelMenu/ColorsPanel';
import SearchMenu from './components/SearchMenu';
import { ViewType } from './components/Header';
import ListView from './components/ListView';
import LocationBaseballCard from './components/LocationBaseballCard';
import LeadSteps from '../../components/LeadDrawer/LeadSteps';

type LeftDrawerView = 'PRACTITIONER_LIST' | 'OPPSANDRISKS' | 'ROUTES' | 'HOME' | 'FUNNEL' | 'MAP' | 'LEAD_SEARCH' | 'LIST_VIEW';
type RightDrawerView = 'PRACTITIONER_DETAILS' | 'LEAD_DETAILS' | 'CREATE_LEAD';

const DesktopLayout: FC = () => {
  const navigate = useNavigate();

  const [mapView, setMapView] = useState<ViewType>('map');
  const [listViewPins, setListViewPins] = useState<any[]>([]);

  const [leftDrawerSelectedView, setLeftDrawerSelectedView] = useState<LeftDrawerView | undefined>();
  const [rightDrawerSelectedView, setRightDrawerSelectedView] = useState<RightDrawerView | undefined>();
  const [footerMenuView, setFooterMenuView] = useState<FooterMenuViewType | undefined>();

  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [mapRouterTab, setMapRouterTab] = useState<MapRouterTab>(MapRouterTab.CURRENT_ROUTE);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedView, setSelectedView] = useState<string | null>(null);
  const [hasRouteChanged, setHasRouteChanged] = useState(false);
  const [selectedPractitioner, setSelectedPractitioner] = useState<any>(null);

  const [storedFilterControl, setFilterControl] = useLazyLocalStorage<FilterControl>
    ("map-filters", DEFAULT_VALUES);
  // EIS-1088: a `map-filters` blob persisted before this ticket has no
  // dataSource/previousSales keys -- normalize to "all toggles on" (inactive) so a
  // stale blob can never produce an empty map. Memoized because MapComponent
  // re-fetches pins whenever the filterControl IDENTITY changes.
  const filterControl = useMemo(
    () => withFilterDefaults(storedFilterControl),
    [storedFilterControl]
  );
  const [colorControl, setColorControl] = useLazyLocalStorage<ColorType>
    ("map-colors");

  const [searchTerm, setSearchTerm] = useState('');

  const [selectedBaseballCardTab, setSelectedBaseballCardTab] = useState<BaseballCardTab>();

  const [selectedLocation, setSelectedLocation] = useState<any | null>(null);
  const [createLeadInfo, setCreateLeadInfo] = useState<any | null>(null);
  const [mapCenterLocation, setMapCenterLocation] = useState<[number, number] | undefined>();
  const [isGoRouteModalOpen, setIsGoRouteModalOpen] = useState(false);
  const [isOnDuplicateView, setIsOnDuplicateView] = useState(false);
  const clearCacheFunctionRef = useRef<() => void | null>();

  const setIsSlideUpMenusOpen = (isOpen: boolean) => {
    store.setIsSlideUpMenusOpenInMap(isOpen);
  };

  const handleCloseLeftSection = () => {
    setLeftDrawerSelectedView(undefined);
    setFooterMenuView(undefined);

    if (mapView === 'list') {
      setMapView('map');
    }
  };

  const handleCloseRightSection = () => {
    setRightDrawerSelectedView(undefined);
    setSelectedLocation(null);
    setCreateLeadInfo(null);
    if (clearCacheFunctionRef.current) {
      clearCacheFunctionRef.current();
    }
  };

  const handleFooterMenuViewChange = (view: FooterMenuViewType) => {
    setFooterMenuView(view);
    setLeftDrawerSelectedView(view);
  };

  const handleShowFunnelMenu = () => {
    setFooterMenuView(undefined);
    setLeftDrawerSelectedView('FUNNEL');
  };

  const handleShowSearchMenu = () => {
    setFooterMenuView(undefined);
    setLeftDrawerSelectedView('LEAD_SEARCH');
  };

  const handleSelectPractitionerInMap = () => {
    setIsSlideUpMenusOpen(false);
    modals.closeAll();
    setRightDrawerSelectedView('PRACTITIONER_DETAILS');
  };

  const handleSelectLeadInMap = (placeId: string) => {
    setIsSlideUpMenusOpen(false);
    modals.closeAll();
    setSelectedLocation(placeId);
    setRightDrawerSelectedView('LEAD_DETAILS');
  };

  const handleToggleMapViewType = () => {
    setIsSlideUpMenusOpen(false);
    modals.closeAll();

    if (mapView === 'map') {
      setLeftDrawerSelectedView('LIST_VIEW');
    } else {
      setLeftDrawerSelectedView(undefined);
    }

    setMapView(prev => prev === 'map' ? 'list' : 'map');
  };

  useEffect(() => {
    setIsSlideUpMenusOpen(false);
    modals.closeAll();

    if (footerMenuView === 'HOME') {
      navigate('/chatbot');
      return;
    }
  }, [footerMenuView, navigate]);

  return (
    <Flex className={styles.wrapper} align="stretch" h="100%">
      {leftDrawerSelectedView && (
        <Paper
          classNames={{
            root: styles.sideSection,
          }}
          style={{ left: 0 }}
        >
          <CloseButton
            classNames={{
              root: styles.closeButton,
            }}
            onClick={handleCloseLeftSection}
          />
          {leftDrawerSelectedView === 'PRACTITIONER_LIST' ? (
            <PractitionersList
              onPractitionerSelect={(initialTab) => {
                setSelectedBaseballCardTab(initialTab);
                setRightDrawerSelectedView('PRACTITIONER_DETAILS');

                const currentPractitioner = store?.currentPractitioner;
                const defaultAddressId = currentPractitioner?.default_address;
                const address = currentPractitioner?.addresses?.find(
                  (x: any) => x.id === defaultAddressId
                );
                if (address && address?.latitude && address?.longitude) {
                  const latLon: [number, number] = [
                    address.latitude,
                    address.longitude
                  ];
                  setMapCenterLocation(latLon);
                  setSelectedPractitioner(currentPractitioner);
                }
              }}
              onOpenLeadCreate={() => {
                setRightDrawerSelectedView('CREATE_LEAD');
              }}
              getFetchPractsAction={(fetchPractsFunction) => {
                clearCacheFunctionRef.current = fetchPractsFunction;
              }}
            />
          ) : leftDrawerSelectedView === 'ROUTES' ? (
            <MapRouter
              setShowUnsavedChangesModal={setShowUnsavedChangesModal}
              showUnsavedChangesModal={showUnsavedChangesModal}
              tab={mapRouterTab}
              setSelectedView={setSelectedView}
              hasRouteChanged={hasRouteChanged}
              setHasRouteChanged={setHasRouteChanged}
              isDesktopMap
              onSelectPractitioner={() => {
                setSelectedBaseballCardTab(BaseballCardTab.SUMMARY);
                setRightDrawerSelectedView('PRACTITIONER_DETAILS');
              }}
            />
          ) : leftDrawerSelectedView === 'OPPSANDRISKS' ? (
            <OppsAndRisk />
          ) : leftDrawerSelectedView === 'FUNNEL' ? (
            <FunnelMenu
              initFilterControl={filterControl}
              initColor={colorControl}
              onApplyFilter={setFilterControl}
              onApplyColor={setColorControl}
            />
          ) : leftDrawerSelectedView === 'LEAD_SEARCH' ? (
            <SearchMenu
              onSearch={setSearchTerm}
            />
          ) : leftDrawerSelectedView === 'LIST_VIEW' ? (
            <ListView
              onPractitionerSelected={(p) => {
                store.setCurrentPractitioner(p);
                handleSelectPractitionerInMap();
                const defaultAddressId = p?.default_address;
                const address = p?.addresses?.find(
                  (x: any) => x.id === defaultAddressId
                );
                if (address && address?.latitude && address?.longitude) {
                  const latLon: [number, number] = [
                    address.latitude,
                    address.longitude
                  ];
                  setMapCenterLocation(latLon);
                  setSelectedPractitioner(p);
                }
              }}
              practitioners={listViewPins}
            />
          ) : null}
        </Paper>
      )}
      <div className={styles.middleSection}>
        <MapComponent
          isDesktop
          isDesktopDetailsOpened={!!rightDrawerSelectedView}
          mapView={mapView}
          onMapViewChange={(v) => setMapView(v)}
          footerMenuView={footerMenuView}
          colorControl={colorControl}
          filterControl={filterControl}
          searchTerm={searchTerm}
          listViewPins={listViewPins}
          mapCenterLocation={mapCenterLocation}
          isGoRouteModalOpen={isGoRouteModalOpen}
          setIsGoRouteModalOpen={setIsGoRouteModalOpen}
          onFooterMenuViewChange={handleFooterMenuViewChange}
          onClickFunnelButton={handleShowFunnelMenu}
          onClickSearchButton={handleShowSearchMenu}
          onSelectPractitioner={handleSelectPractitionerInMap}
          onSelectLead={handleSelectLeadInMap}
          toggleViewType={handleToggleMapViewType}
          onListViewPinsChange={setListViewPins}
          //pin states
          selectedPractitioner={selectedPractitioner}
          onSelectedPractitioner={(p) => setSelectedPractitioner(p)}
        />
      </div>
      {rightDrawerSelectedView && (
        <Paper
          classNames={{
            root: styles.sideSection,
          }}
          style={{ right: 0 }}
        >
          <CloseButton
            classNames={{
              root: styles.closeButton,
            }}
            onClick={handleCloseRightSection}
          />
          {rightDrawerSelectedView === 'PRACTITIONER_DETAILS' ? (
            <BaseballCard
              backHandler={() => {
                // return to lead in case of view duplicate
                if (isOnDuplicateView) {
                  setRightDrawerSelectedView("CREATE_LEAD");
                  setIsOnDuplicateView(false);
                } else {
                  setRightDrawerSelectedView(undefined);
                }
              }}
              defaultTab={selectedBaseballCardTab ?? BaseballCardTab.SUMMARY}
              generalNotes=""
              onFindOnMap={(p, latLon) => {
                setMapCenterLocation(latLon);
                setSelectedPractitioner(p);
              }}
            />
          ) : rightDrawerSelectedView === 'LEAD_DETAILS' ? (
            <LocationBaseballCard
              placeId={selectedLocation}
              onCreateLead={(li) => {
                setCreateLeadInfo(li);
                setRightDrawerSelectedView('CREATE_LEAD');
              }}
              onCenterOnMap={setMapCenterLocation}
              onFindOnMap={() => setIsGoRouteModalOpen(true)}
              onPhoneClicked={(phone) => {
                window.open(`tel:${phone}`, '_blank');
              }}
            />
          ) : rightDrawerSelectedView === 'CREATE_LEAD' ? (
            <LeadSteps
              initialSearchValue=""
              practitionerInfo={createLeadInfo}
              isDesktopMap
              onFormStateChange={() => {}}
              onFormViewChange={() => {}}
              onClose={handleCloseRightSection}
              onViewDuplicate={() => {
                setIsOnDuplicateView(true);
                setRightDrawerSelectedView("PRACTITIONER_DETAILS");
              }}
            />
          ) : null}
        </Paper>
      )}
    </Flex>
  );
};

export default DesktopLayout;
